/// <amd-module name="VSSUI/Utilities/ViewState" />
import { IObservable, IObservableValue, Observable } from "VSS/Core/Observable";
import { IFilterState, IFilterOptions, Filter, FILTER_CHANGE_EVENT } from "VSSUI/Utilities/Filter";
import { IViewOptionsProps, ViewOptions } from "./ViewOptions";

/**
 * Names of events fired by view state
 */
/** @deprecated */
export namespace ViewStateEventNames {
    /**
     * One or more members of the view state is changing
     */
    /** @deprecated */
export const viewStateChanging = "viewStateChanging";

    /**
     * One or more members of the view state was changed.
     */
    /** @deprecated */
export const viewStateChanged = "viewStateChanged";
}

/**
 * Event object sent in view state changing and view state changed events.
 */
/** @deprecated */
export interface IViewStateChangeEvent<T> {
    /**
     * Name of the view-state event that triggered the view-state changing/changed events
     */
    eventName: string;

    /**
     * The value of view-state event that triggered teh view-state changing/changed events
     */
    eventValue: T;
}

/**
 * Store for properties of a view
 */
/** @deprecated */
export interface IViewState<T> extends IObservable<T | IViewStateChangeEvent<T>> {}

interface IQueuedViewStateEvent<T> {
    observable: IObservable<T>;
    eventName: string;
    eventValue: T;
    notify: { (value: T, action: string): void };
}

/**
 * Delegate to notify a view-state one of its values is chaning.
 */
/** @deprecated */
export interface IViewStateNotifyDelegate<T> {
    /**
     * Method to notify view-state listeners that a state value is changing.
     *
     * @param eventValue The value of the state that was changed
     * @param viewStateEventName The name of the state-change event to fire
     * @param observable The observable that is firing the event
     * @param observableEventName Name of the specific event that should be triggered on the observable
     * @param observableNotify Method to directly notify the observable's consumers
     */
    (
        eventValue: T,
        viewStateEventName: string,
        observable: IObservable<T | IViewStateChangeEvent<T>>,
        observableEventName: string,
        observableNotify: { (value: T, action: string): void }
    ): void;
}

/**
 * Store for properties of a view
 */
/** @deprecated */
export class ViewState<T> implements IViewState<T> {
    private observable = new Observable<T | IViewStateChangeEvent<T>>();
    private changingEvents: IQueuedViewStateEvent<T>[];

    public subscribe(observer: (value: T, action: string) => void, action?: string): (value: T, action: string) => void {
        return this.observable.subscribe(observer, action);
    }

    public unsubscribe(observer: (value: T, action: string) => void, action?: string): void {
        this.observable.unsubscribe(observer, action);
    }

    /**
     * Notify listeners that a state value is changing. The sequence of events goes like this:
     *
     * 1. The 'viewStateChanging' event is fired
     * 2. The 'viewStateEventName' event is fired, letting consumers update state in reaction to the change.
     *    While these listeners are processing, individual state change events are suppresed.
     * 3. The observable event specified here is triggered
     * 4. The events from any state changed as part of the event listeners in step 2 are triggered.
     * 5. The 'viewStateChanged' event is fired
     *
     * @param eventValue The value of the state that was changed
     * @param viewStateEventName The name of the state-change event to fire
     * @param observable The observable that is firing the event
     * @param observableEventName Name of the specific event that should be triggered on the observable
     * @param observableNotify Method to directly notify the observable's consumers
     */
    protected notify(
        eventValue: T,
        viewStateEventName: string,
        observable: IObservable<T>,
        observableEventName: string,
        observableNotify: { (value: T, action: string): void }
    ) {
        if (this.changingEvents) {
            // Already in the middle of a change event. Just queue the event.
            if (observable && observableNotify) {
                // If we have an existing event with this same observable/eventName, just update its value.
                let updatedExistingEvent = false;
                for (let existingEvent of this.changingEvents) {
                    if (existingEvent.observable === observable && existingEvent.eventName === observableEventName) {
                        const viewStateObservable = observable as IViewStateObservable<T>;
                        if (viewStateObservable.mergeChangeEvents) {
                            existingEvent.eventValue = viewStateObservable.mergeChangeEvents(existingEvent.eventValue, eventValue);
                        } else {
                            existingEvent.eventValue = eventValue;
                        }
                        updatedExistingEvent = true;
                        break;
                    }
                }

                // New observable/event. Add it to our queue.
                if (!updatedExistingEvent) {
                    this.changingEvents.push({
                        observable: observable,
                        eventName: observableEventName,
                        eventValue: eventValue,
                        notify: observableNotify
                    });
                }
            }
        } else {
            // Create a queue of callbacks. This queue will bock (but remember) events
            // from individual observables from firing until all the view-state-change
            // event listers have completed. Then the previously blocked events will be emitted.
            this.changingEvents = [];

            // Add this observable event first
            if (observable && observableNotify) {
                this.changingEvents.push({
                    observable: observable,
                    eventName: observableEventName,
                    eventValue: eventValue,
                    notify: observableNotify
                });
            }

            const viewStateChangeEvent: IViewStateChangeEvent<T> = {
                eventName: viewStateEventName,
                eventValue: eventValue
            };

            // Fire an event to indicate that a view-state-change is starting
            this.observable.notify(viewStateChangeEvent, ViewStateEventNames.viewStateChanging);

            // Fire a change-start event
            this.observable.notify(eventValue, viewStateEventName);

            // Fire the individual callbacks that we previously suppressed
            for (let changingEvent of this.changingEvents) {
                changingEvent.notify(changingEvent.eventValue, changingEvent.eventName);
            }

            // Delete the changing events queue. The next change event will start this process.
            this.changingEvents = null;

            // Fire an event to indicate that a view-state-change is complete
            this.observable.notify(viewStateChangeEvent, ViewStateEventNames.viewStateChanged);
        }
    }
}

/**
 * An observable value which is used in a view state
 */
/** @deprecated */
export interface IViewStateObservable<T> extends IObservable<T> {
    /**
     * Method called when more than one of a particular event is triggered
     * for a single view-state-change operation. The default behavior is that the
     * most-recent value is taken.
     */
    mergeChangeEvents: (firstEventValue: T, nextEventValue: T) => T;
}

/**
 * An observable value which is used in a view state
 */
/** @deprecated */
export class ViewStateObservable<T> extends Observable<T> implements IViewStateObservable<T> {
    private notifyViewStateDelegate: IViewStateNotifyDelegate<T>;
    private viewStateEventName: string;
    private mergeEventsAsObjects: boolean;

    /**
     * Create a new view state observable
     *
     * @param notifyViewStateDelegate The view state's notify method
     * @param viewStateChangeEventName The name of the view state change event
     */
    constructor(
        notifyViewStateDelegate: IViewStateNotifyDelegate<T>,
        viewStateChangeEventName: string,
        mergeEventsAsObjects?: boolean,
        bypassCallback?: (value: T, action: string) => boolean
    ) {
        super();

        this.notifyViewStateDelegate = notifyViewStateDelegate;
        this.viewStateEventName = viewStateChangeEventName;
        this.mergeEventsAsObjects = mergeEventsAsObjects;
    }

    public notify(value: T, action: string, persistEvent?: boolean) {
        if (this.shouldBypassViewState(value, action, persistEvent)) {
            super.notify(value, action, persistEvent);
        } else {
            this.notifyViewStateDelegate(value, this.viewStateEventName, this, action, super.notify.bind(this));
        }
    }

    protected shouldBypassViewState(value: T, action: string, persistEvent?: boolean) {
        return false;
    }

    public mergeChangeEvents(firstEventValue: T, nextEventValue: T): T {
        return nextEventValue;
    }
}

/**
 * An observable value which is used in a view state
 */
/** @deprecated */
export class ViewStateObservableValue<T> extends ViewStateObservable<T> implements IObservableValue<T> {
    private v: T;

    constructor(notifyViewStateDelegate: IViewStateNotifyDelegate<T>, viewStateChangeEventName: string, value?: T) {
        super(notifyViewStateDelegate, viewStateChangeEventName);
        this.v = value;
    }

    public get value(): T {
        return this.v;
    }

    public set value(value: T) {
        if (value !== this.v) {
            this.v = value;
            this.notify(value, "set");
        }
    }
}

/**
 * Filter used within a view state
 */
/** @deprecated */
export class ViewStateFilter extends Filter {
    constructor(notifyViewStateDelegate: IViewStateNotifyDelegate<IFilterState>, viewStateChangeEventName: string, options?: IFilterOptions) {
        super(options, new ViewStateFilterObservable(notifyViewStateDelegate, viewStateChangeEventName));
    }
}

class ViewStateFilterObservable extends ViewStateObservable<IFilterState> {
    protected shouldBypassViewState(value: IFilterState, action: string, persistEvent?: boolean) {
        return action !== FILTER_CHANGE_EVENT;
    }

    public mergeChangeEvents(firstEventValue: IFilterState, nextEventValue: IFilterState): IFilterState {
        return { ...firstEventValue, ...nextEventValue };
    }
}

/**
 * ViewOptions used within a view state
 */
/** @deprecated */
export class ViewStateOptions extends ViewOptions {
    constructor(notifyViewStateDelegate: IViewStateNotifyDelegate<IFilterState>, viewStateChangeEventName: string, options?: IViewOptionsProps) {
        super(options, new ViewStateOptionsObservable(notifyViewStateDelegate, viewStateChangeEventName));
    }
}

class ViewStateOptionsObservable extends ViewStateObservable<IFilterState> {
    public mergeChangeEvents(firstEventValue: IFilterState, nextEventValue: IFilterState): IFilterState {
        return { ...firstEventValue, ...nextEventValue };
    }
}
